import { cn } from '@/lib/utils';
import { useContext } from 'react';

import { ChainlitContext } from '@chainlit/react-client';

type BrandLogoVariant = 'mark' | 'wordmark';
type BrandLogoSize = 'avatar' | 'header' | 'launcher' | 'welcome';

interface BrandLogoProps {
  className?: string;
  size: BrandLogoSize;
  variant: BrandLogoVariant;
}

const markSizes: Record<BrandLogoSize, string> = {
  avatar: 'size-8 rounded-lg p-1',
  header: 'size-9 rounded-xl p-1.5',
  launcher: 'size-14 rounded-full p-2.5',
  welcome: 'size-12 rounded-xl p-2'
};

const wordmarkSizes: Record<BrandLogoSize, string> = {
  avatar: 'h-7 w-[84px]',
  header: 'h-7 w-[84px]',
  launcher: 'h-10 w-[120px]',
  welcome: 'h-12 w-36'
};

const BrandLogo = ({ className, size, variant }: BrandLogoProps) => {
  const apiClient = useContext(ChainlitContext);
  const assetUrl = (filename: string) =>
    apiClient.buildEndpoint(`/public/${filename}`);

  if (variant === 'mark') {
    return (
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex shrink-0 items-center justify-center border border-slate-200/90 bg-white',
          markSizes[size],
          className
        )}
      >
        <img
          src={assetUrl('favicon.png')}
          alt=""
          className="h-full w-full object-contain"
        />
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 items-center justify-center',
        wordmarkSizes[size],
        className
      )}
    >
      <img
        src={assetUrl('logo_light.png')}
        alt=""
        className="h-full w-full object-contain dark:hidden"
      />
      <img
        src={assetUrl('logo_dark.png')}
        alt=""
        className="hidden h-full w-full object-contain dark:block"
      />
    </span>
  );
};

export { BrandLogo };
